import { useParams, Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { AlertTriangle, Clock, Search, X } from "lucide-react";
import { TTSButton } from "../components/TTSButton";
import { useState } from "react";

/**
 * Fiches Réflexes — 4 dossiers civils/locatifs cohérents avec le produit.
 * Contenu informatif neutre (Loi 71-1130). Aucun conseil juridique personnalisé.
 * Données 100 % locales (aucun appel API).
 */
const FICHES = [
  {
    id: "depot_garantie",
    title: "Dépôt de garantie : Délais légaux et retenues injustifiées",
    urgence: "haute",
    duree_lecture_min: 4,
    sections: [
      { titre: "Délais légaux (Loi 89-462, art. 22)", points: [
        "Le bailleur dispose d'un délai maximum d'1 mois si l'état des lieux de sortie est conforme à celui d'entrée.",
        "Le délai est porté à 2 mois si l'état des lieux de sortie mentionne des dégradations.",
        "Toute retenue doit être justifiée par des devis ou factures probants et détaillés.",
      ]},
      { titre: "Majoration en cas de retard", points: [
        "10 % du montant du dépôt de garantie par mois de retard commencé.",
        "Court automatiquement sans mise en demeure préalable.",
      ]},
      { titre: "Réflexes utiles", points: [
        "Envoyer une mise en demeure en LRAR avec RIB et rappel du délai légal.",
        "Conserver copie de l'état des lieux, du bail et de l'attestation de restitution des clés.",
        "En cas d'échec amiable : Commission Départementale de Conciliation (gratuite) puis tribunal judiciaire.",
      ]},
    ],
  },
  {
    id: "regularisation_charges",
    title: "Régularisation de charges : Les justificatifs annuels obligatoires",
    urgence: "haute",
    duree_lecture_min: 4,
    sections: [
      { titre: "Obligation du bailleur (Loi 89-462, art. 23)", points: [
        "Communication à la disposition du locataire des justificatifs de charges pendant 6 mois après l'envoi du décompte.",
        "Décompte individuel par nature de charges et par local, avec grille de tantièmes.",
        "En copropriété : arrêté des comptes approuvé en Assemblée Générale.",
      ]},
      { titre: "Ce que vous pouvez demander", points: [
        "Copie du décompte individuel et des factures des prestataires (eau, entretien, ascenseur).",
        "Répartition détaillée par lot et clé de calcul appliquée.",
      ]},
      { titre: "Recours en cas d'absence", points: [
        "Lettre de mise en demeure avec délai raisonnable (15 jours).",
        "À défaut : suspension légitime du paiement jusqu'à communication.",
        "Si régularisation abusive : contestation devant la Commission Départementale de Conciliation.",
      ]},
    ],
  },
  {
    id: "fps",
    title: "Forfait Post-Stationnement (FPS) : Contester une erreur d'avis",
    urgence: "moyenne",
    duree_lecture_min: 3,
    sections: [
      { titre: "Cadre légal (CGCT, art. L. 2333-87)", points: [
        "Le FPS est une redevance d'occupation du domaine public, pas une amende pénale.",
        "Recours Administratif Préalable Obligatoire (RAPO) dans les 30 jours.",
        "Puis Commission du Contentieux du Stationnement Payant (CCSP).",
      ]},
      { titre: "Motifs classiques de contestation", points: [
        "Ticket payé au moment du contrôle (dématérialisé ou horodateur).",
        "Erreur de saisie sur la plaque d'immatriculation.",
        "Véhicule cédé ou volé au moment des faits.",
      ]},
      { titre: "Constituer votre dossier", points: [
        "Justificatif bancaire du paiement (relevé, capture).",
        "Reçu électronique de l'application de stationnement.",
        "Photographie du panneau de zone si nécessaire.",
      ]},
    ],
  },
  {
    id: "vetuste_degradation",
    title: "Vétusté vs Dégradation : Grille d'usure locative",
    urgence: "normale",
    duree_lecture_min: 4,
    sections: [
      { titre: "Définitions clés", points: [
        "Vétusté : usure normale liée au temps et à l'usage courant. À la charge du bailleur.",
        "Dégradation : détérioration anormale imputable au locataire (négligence, mauvais entretien, casse).",
      ]},
      { titre: "Grille de référence (usage courant)", points: [
        "Peinture murale : renouvellement conseillé tous les 7 à 10 ans.",
        "Sols souples (linoléum, PVC) : durée de vie moyenne d'environ 10 ans.",
        "Sanitaires (joints, robinetterie) : entretien locatif régulier attendu.",
      ]},
      { titre: "Réflexes locataire", points: [
        "Exiger une grille de vétusté annexée au bail (obligatoire pour les baux relevant de la loi Alur).",
        "Photographier soigneusement l'état des lieux d'entrée et de sortie.",
        "Contester par LRAR toute retenue disproportionnée sur le dépôt de garantie.",
      ]},
    ],
  },
];

const FICHES_MAP = Object.fromEntries(FICHES.map((f) => [f.id, f]));

export function FichesIndex() {
  const [query, setQuery] = useState("");
  const normalize = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const q = normalize(query);
  const filtered = q ? FICHES.filter((f) => normalize(f.title).includes(q) || normalize(f.id).includes(q)) : FICHES;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <span className="j-label">Fiches Réflexes · 4 dossiers civils clés</span>
        <h1 className="j-h1 font-heading" data-testid="fiches-title">Fiches Réflexes</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">4 dossiers civils fondamentaux : caution, charges, FPS et vétusté. Contenu informatif neutre (Loi 71-1130).</p>

        <div className="relative mt-6" data-testid="fiches-search">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input type="search" className="j-input pl-9 pr-9" placeholder="Rechercher (caution, charges, FPS…)" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="fiches-search-input" />
          {query && <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="fiches-search-clear" aria-label="Effacer"><X className="w-4 h-4" /></button>}
        </div>
        <div className="text-xs text-slate-500 mt-2" data-testid="fiches-search-results">{filtered.length} fiche(s) sur {FICHES.length}</div>

        <div className="grid md:grid-cols-2 gap-4 mt-6">
          {filtered.map((f) => {
            const uColor = f.urgence === "haute" ? "#F59E0B" : "#10B981";
            return (
              <Link key={f.id} to={`/fiches/${f.id}`} className="j-card hover:border-[#10B981] transition-colors" data-testid={`fiche-link-${f.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="j-label" style={{ color: uColor }}>{f.urgence}</span>
                  <span className="text-xs text-slate-500 ml-auto"><Clock className="w-3 h-3 inline mr-1" />{f.duree_lecture_min} min de lecture</span>
                </div>
                <h3 className="font-heading font-black text-lg text-[#1E293B]">{f.title}</h3>
              </Link>
            );
          })}
          {filtered.length === 0 && <div className="col-span-2 j-card text-center text-slate-500" data-testid="fiches-empty">Aucune fiche ne correspond à « <b>{query}</b> ».</div>}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function FicheDetail() {
  const { ficheId } = useParams();
  const fiche = FICHES_MAP[ficheId];
  if (!fiche) return <div className="min-h-screen"><Header /><div className="max-w-3xl mx-auto p-10">Fiche introuvable.</div><Footer /></div>;
  const uColor = fiche.urgence === "haute" ? "#F59E0B" : "#10B981";
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/fiches" className="text-sm text-slate-600 hover:underline">← Toutes les fiches</Link>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <span className="evidence-pill" style={{ background: uColor + "20", color: uColor, borderColor: uColor + "40" }}><AlertTriangle className="w-3 h-3" /> {fiche.urgence}</span>
          <TTSButton label="Écouter cette fiche" getText={() => {
            const parts = [fiche.title];
            fiche.sections.forEach((s) => { parts.push(s.titre + "."); s.points.forEach((p) => parts.push(p)); });
            return parts.join(". ");
          }} />
        </div>
        <h1 className="j-h1 font-heading mt-2" data-testid="fiche-title">{fiche.title}</h1>
        <div className="mt-8 space-y-6">
          {fiche.sections.map((s, i) => (
            <div key={i} className="j-card" data-testid={`fiche-section-${i}`}>
              <h2 className="font-heading font-black text-xl text-[#1E293B] mb-3">{s.titre}</h2>
              <ul className="space-y-2">
                {s.points.map((p, j) => (
                  <li key={j} className="text-sm text-slate-700 leading-relaxed flex gap-2">
                    <span className="text-[#10B981] font-bold flex-shrink-0">✓</span> {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
